/**
 * /admin/pipeline/:id — operators: one video in the pipeline. The step its
 * job is on and that step's counters, every step so far with its time, the
 * running attempt's log as the worker writes it (live files in R2, streamed
 * by GET /ops/jobs/:job/attempts/:n/live/events, about 5 s behind the pod),
 * the annotated calibration frame, and the video's jobs. The page follows the
 * running job, else the newest pipeline job (`mainJob`), so a finished export
 * or players backfill does not hide the analysis's steps; any job in the list
 * can be opened instead.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { get, post } from "../api/client";
import { useLoad, usePoll } from "../api/hooks";
import type { LiveFile, PipelineAttempt, PipelineDetail, PipelineStep, ProgressCounter } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Shell } from "../components/Shell";
import { Bar, Dot, ErrorLine, Seg } from "../components/ui";
import { fmtDuration } from "../lib/format";
import { STALE_S, ago, counterShare, counterText, fmtSeconds, fmtWhen, mainJob, partLabel, secondsSince, stateWords, stepDot, stepLabel, taskWords, whereIs } from "../lib/pipeline";
import { AdminSpotlight } from "../components/Spotlight";
import { OperatorsOnly } from "./AdminPipeline";

/** Lines kept in the page; older ones are dropped (the full log is uploaded when the run ends). */
const MAX_LINES = 5000;
/** Live files shown before the newest when the page opens (about 100 s at one file per 5 s). */
const BACKFILL = 20;

export default function AdminPipelineVideo() {
  const { id = "" } = useParams();
  const { me } = useAuth();
  const detail = useLoad(() => get<PipelineDetail>(`/ops/videos/${id}/pipeline`), [id]);
  const reload = useCallback(() => detail.reload(), [detail.reload]);
  usePoll(reload, 15_000, !!me?.user.is_operator);
  const d = detail.data;
  const [picked, setPicked] = useState<string | null>(null);
  const job = (picked ? d?.jobs.find((j) => j.id === picked) : undefined) ?? mainJob(d?.jobs ?? []);
  const attempt = job?.attempts[0] ?? null;
  const running = !!attempt && job?.state === "running" && (attempt.state === "leased" || attempt.state === "running");
  const log = useLiveLog(job?.id ?? null, attempt?.n ?? null, running, attempt?.live?.seq ?? 0);

  if (me && !me.user.is_operator) return <OperatorsOnly />;
  if (!d) {
    return (
      <Shell back="/admin/pipeline" title="Pipeline">
        <div className="p" style={{ paddingTop: 24 }}>
          <ErrorLine error={detail.error} />
          {!detail.error && <p className="tiny">Loading…</p>}
        </div>
      </Shell>
    );
  }

  // the freshest view of the step: the newest live file while streaming, else what the worker posted
  const lastFile = log.last;
  const fromFile = running && lastFile && lastFile.steps ? lastFile : null;
  const steps: PipelineStep[] = fromFile?.steps ?? attempt?.steps ?? [];
  const step = fromFile ? fromFile.step ?? null : attempt?.progress?.step ?? null;
  const part = fromFile ? fromFile.part ?? null : attempt?.progress?.part ?? null;
  const counter: ProgressCounter | null = fromFile ? fromFile.progress ?? null : attempt?.progress?.progress ?? null;
  const secondary: ProgressCounter | null = fromFile ? fromFile.secondary ?? null : attempt?.progress?.secondary ?? null;
  const updated = fromFile?.t ?? attempt?.progress?.updated_at ?? attempt?.updated_at ?? d.video.updated_at;
  const where = whereIs(d.video.state, job, running ? { ...(attempt?.progress ?? emptyProgress(updated)), step } : null, attempt?.state);
  const share = counterShare(counter);
  const stale = running && secondsSince(attempt?.worker_seen_at ?? updated) > STALE_S;

  return (
    <Shell back="/admin/pipeline" title="Pipeline">
      <div className="p pipe" style={{ paddingTop: 24, paddingBottom: 48 }} data-testid="admin-pipeline-video">
        <p className="cap"><Link to="/admin/pipeline">Pipeline</Link></p>
        <h1 className="mt6">{d.video.title}</h1>
        <p className="sub mt10">
          {d.uploader.email}{d.uploader.via === "workspace_owner" ? " (workspace owner)" : ""} · {d.workspace.plan} · {stateWords(d.video.state)}
          {d.video.duration_ms ? ` · ${fmtDuration(d.video.duration_ms)}` : ""}
        </p>
        <p className="tiny mt6">{d.video.id}{d.video.image_size ? ` · ${d.video.image_size[0]}×${d.video.image_size[1]}` : ""}{d.video.fps ? ` · ${d.video.fps.toFixed(2)} fps` : ""}</p>
        {d.intake && (
          <p className="tiny mt6" data-testid="pipeline-intake">
            Player Spotlight: {d.intake.scorecard_name ? `${d.intake.scorecard_name} on the ` : ""}
            {d.intake.scorecard_url ? <a className="doc" href={d.intake.scorecard_url} target="_blank" rel="noreferrer">scorecard</a> : "no scorecard"}
            {" · "}under 18 {d.intake.minors === "yes" ? "yes, a parent or guardian agreed" : "no"}
            {" · "}{d.intake.social ? `${d.intake.social.scope === "social_media" ? "Social media yes" : "Instagram-only yes"}, ${d.intake.social.instagram_handle ? `tag @${d.intake.social.instagram_handle}` : "no handle"}` : "Social media no"}
          </p>
        )}

        <AdminSpotlight videoId={id} />
        <div className="pipe-grid mt32">
          <div className="pipe-main">
            <section className="card now" data-testid="pipeline-now">
              <p className="cap">{running ? "Now" : "State"}</p>
              <div className="row mt10" style={{ gap: 0 }}><Dot kind={where.dot} /><span className="h2">{where.label}</span></div>
              {where.detail && <p className="tiny mt6">{where.detail}</p>}
              {running && counter && (
                <>
                  <div className="big mt16 num" data-testid="pipeline-counter">{counterText(counter)}</div>
                  {share != null && <div className="mt10"><Bar value={share} /></div>}
                  <p className="sub mt10">{[partLabel(part), secondary ? counterText(secondary) : ""].filter(Boolean).join(" · ")}</p>
                </>
              )}
              {!running && d.video.error && <p className="err mt10">{d.video.error.code}{d.video.error.message ? `: ${d.video.error.message}` : ""}</p>}
              {d.video.state === "ingest_failed" && <Reingest videoId={d.video.id} reload={reload} />}
              <p className={`tiny mt10 ${stale ? "bad" : ""}`}>
                Updated {ago(updated)}{attempt?.pod_id ? ` · ${attempt.pod_id}` : ""}{stale ? " · no word from the worker for over 2 min" : ""}
              </p>
            </section>

            {steps.length > 0 && (
              <section className="mt32" data-testid="pipeline-steps">
                <h3>Steps <span className="tiny" style={{ fontWeight: 400 }}>{job ? taskWords(job.task) : ""}</span></h3>
                <div className="list mt10">
                  {steps.map((s) => (
                    <div key={s.step} className="step-row" data-state={s.state}>
                      <span><Dot kind={stepDot(s)} />{stepLabel(s.step)}</span>
                      <span className="tiny" style={{ marginLeft: "auto", textAlign: "right" }}>
                        {s.progress ? counterText(s.progress) : ""}
                        {s.progress && (s.seconds != null || s.state === "running") ? " · " : ""}
                        {s.state === "running" ? `running ${fmtSeconds(secondsSince(s.started_at))}` : s.state === "skipped" ? "skipped" : fmtSeconds(s.seconds)}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <LogPanel log={log} running={running} attempt={attempt} currentStep={step} />
          </div>

          <aside className="pipe-side">
            <Calibration cal={d.calibration} calibrated={!["uploading", "uploaded", "ingesting", "ingest_failed", "calibration_queue", "calibrating"].includes(d.video.state)} />
            <section className="mt32" data-testid="pipeline-jobs">
              <h3>Jobs</h3>
              <div className="list mt10">
                {d.jobs.map((j) => {
                  const a = j.attempts[0];
                  const took = j.started_at ? ((j.finished_at ? new Date(j.finished_at).getTime() : Date.now()) - new Date(j.started_at).getTime()) / 1000 : null;
                  const shown = j.id === job?.id;
                  return (
                    <div key={j.id} className="stack job-row" data-shown={shown} style={{ alignItems: "stretch", gap: 4 }}>
                      <div className="row between">
                        <button className="job-pick" onClick={() => setPicked(j.id)} aria-pressed={shown} title={shown ? "Shown above" : "Show its steps and log"}>
                          <Dot kind={j.state === "succeeded" ? "ok" : j.state === "failed" ? "bad" : j.state === "running" ? "on" : ""} />{taskWords(j.task)}
                        </button>
                        <span className="tiny">{j.state}{j.attempt > 1 ? ` · attempt ${j.attempt} of ${j.max_attempts}` : ""}</span>
                      </div>
                      <div className="tiny">
                        {fmtWhen(j.created_at)}{took != null ? ` · ${fmtSeconds(took)}` : ""}{a?.pod_id ? ` · ${a.pod_id}` : ""}
                        {j.queue_position ? ` · ${j.queue_position} in the ${j.pool.toUpperCase()} queue` : ""}
                      </div>
                      {j.error && <div className="tiny bad">{j.error.code}{j.error.message ? `: ${j.error.message.slice(0, 160)}` : ""}</div>}
                      {a?.log_url && <a className="tiny u" href={a.log_url} target="_blank" rel="noreferrer">Full log (attempt {a.n})</a>}
                    </div>
                  );
                })}
                {d.jobs.length === 0 && <div className="tiny">No jobs yet.</div>}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </Shell>
  );
}

function emptyProgress(updated: string) {
  return { phase: null, step: null, part: null, progress: null, secondary: null, message: null, live_seq: null, updated_at: updated };
}

/* ------------------------------------------------------------ the log */

interface LiveLog {
  files: LiveFile[];
  last: LiveFile | null;
  status: "idle" | "live" | "reconnecting" | "ended";
  error: Error | null;
  earlier: () => Promise<void>;
  canEarlier: boolean;
}

/**
 * The attempt's live files: followed over SSE while it runs (the browser
 * reconnects with Last-Event-ID after each 60 s stream), else the newest page
 * read once. `seq` is only where to start, so the stream is not restarted by
 * each refresh of the page's data.
 */
function useLiveLog(jobId: string | null, n: number | null, running: boolean, seq: number): LiveLog {
  const [files, setFiles] = useState<LiveFile[]>([]);
  const [status, setStatus] = useState<LiveLog["status"]>("idle");
  const [error, setError] = useState<Error | null>(null);
  // where to start, per attempt: the operator can open another job's log
  const startSeq = useRef(seq);
  const startOf = useRef(`${jobId}/${n}`);
  if (startOf.current !== `${jobId}/${n}`) [startOf.current, startSeq.current] = [`${jobId}/${n}`, seq];
  startSeq.current = startSeq.current || seq;
  const add = useCallback((more: LiveFile[], atStart = false) => {
    setFiles((prev) => {
      const have = new Set(prev.map((f) => f.seq));
      const fresh = more.filter((f) => !have.has(f.seq));
      if (!fresh.length) return prev;
      let next = atStart ? [...fresh, ...prev] : [...prev, ...fresh];
      next.sort((a, b) => a.seq - b.seq);
      let lines = next.reduce((k, f) => k + f.lines.length, 0);
      while (lines > MAX_LINES && next.length > 1 && !atStart) {
        lines -= next[0]!.lines.length;
        next = next.slice(1);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setFiles([]);
    setError(null);
    if (!jobId || !n) return;
    const base = `/ops/jobs/${encodeURIComponent(jobId)}/attempts/${n}/live`;
    if (!running) {
      setStatus("ended");
      const before = (startSeq.current || 0) + 1;
      if (before > 1) {
        get<{ files: LiveFile[] }>(`${base}?before=${before}&limit=30`).then((r) => add(r.files), (e) => setError(e as Error));
      }
      return;
    }
    const es = new EventSource(`${base}/events?after=${Math.max(0, (startSeq.current || 0) - BACKFILL)}`);
    setStatus("live");
    es.addEventListener("live", (ev) => {
      try {
        add([JSON.parse((ev as MessageEvent).data) as LiveFile]);
        setStatus("live");
      } catch {
        /* a file that does not parse is skipped */
      }
    });
    es.addEventListener("bye", (ev) => {
      try {
        const bye = JSON.parse((ev as MessageEvent).data) as { reconnect: boolean };
        if (!bye.reconnect) {
          es.close();
          setStatus("ended");
        }
      } catch {
        /* keep the connection */
      }
    });
    es.onerror = () => setStatus((s) => (s === "ended" ? s : "reconnecting"));
    return () => es.close();
  }, [jobId, n, running, add]);

  const first = files[0]?.seq ?? 0;
  const earlier = useCallback(async () => {
    if (!jobId || !n || first <= 1) return;
    try {
      const r = await get<{ files: LiveFile[] }>(`/ops/jobs/${encodeURIComponent(jobId)}/attempts/${n}/live?before=${first}&limit=30`);
      add(r.files, true);
    } catch (e) {
      setError(e as Error);
    }
  }, [jobId, n, first, add]);
  return { files, last: files[files.length - 1] ?? null, status, error, earlier, canEarlier: first > 1 };
}

function LogPanel({ log, running, attempt, currentStep }: { log: LiveLog; running: boolean; attempt: PipelineAttempt | null; currentStep: string | null }) {
  // a finished attempt has no current step to narrow to
  const [only, setOnly] = useState<"step" | "all">(running ? "step" : "all");
  const box = useRef<HTMLDivElement>(null);
  const [follow, setFollow] = useState(true);
  const rows = useMemo(() => {
    const out: { key: string; sep?: string; text?: string }[] = [];
    let prevStep: string | undefined;
    for (const f of log.files) {
      // each line under the step that ran when it arrived (older files: the file's step)
      const marks = f.marks?.length ? f.marks : [[0, f.step ?? ""] as [number, string]];
      let m = 0;
      f.lines.forEach((text, i) => {
        while (m + 1 < marks.length && marks[m + 1]![0] <= i) m++;
        const s = marks[m]![1] || prevStep || "";
        if (only === "step" && currentStep && s !== currentStep) return;
        if (s !== prevStep) {
          out.push({ key: `sep-${f.seq}-${i}`, sep: s ? stepLabel(s) : "Starting" });
          prevStep = s;
        }
        out.push({ key: `${f.seq}-${i}`, text });
      });
    }
    return out;
  }, [log.files, only, currentStep]);
  useEffect(() => {
    if (follow && box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [rows, follow]);
  const onScroll = () => {
    const el = box.current;
    if (el) setFollow(el.scrollHeight - el.scrollTop - el.clientHeight < 24);
  };
  const statusWord = log.status === "live" ? "following" : log.status === "reconnecting" ? "reconnecting…" : running ? "" : "run ended";
  return (
    <section className="mt32" data-testid="pipeline-log">
      <div className="row between wrap-r" style={{ rowGap: 8 }}>
        <h3>Log <span className="tiny" style={{ fontWeight: 400 }}>{statusWord}</span></h3>
        <div style={{ width: 220 }}>
          <Seg value={only} onChange={setOnly} options={[{ value: "step", label: "This step" }, { value: "all", label: "Everything" }]} />
        </div>
      </div>
      <ErrorLine error={log.error} />
      <div className="logbox mt10" ref={box} onScroll={onScroll} data-testid="pipeline-logbox">
        {log.canEarlier && <button className="log-more" onClick={() => void log.earlier()}>Show earlier lines</button>}
        {rows.length === 0 && (
          <div className="log-empty">
            {!attempt
              ? "No attempt has run yet."
              : only === "step" && log.files.some((f) => f.lines.length)
                ? <>Nothing logged under {currentStep ? stepLabel(currentStep) : "this step"} yet. <button className="log-more" style={{ display: "inline", padding: 0 }} onClick={() => setOnly("all")}>Show everything</button></>
                : running ? "Waiting for the first lines…" : "No live log for this attempt. The full log is under Jobs."}
          </div>
        )}
        {rows.map((r) => (r.sep ? <div key={r.key} className="log-sep">{r.sep}</div> : <div key={r.key} className="log-line">{r.text || " "}</div>))}
      </div>
      {!follow && rows.length > 0 && (
        <button className="btn sm ghost mt10" onClick={() => { setFollow(true); if (box.current) box.current.scrollTop = box.current.scrollHeight; }}>Jump to the newest line</button>
      )}
    </section>
  );
}

/* ------------------------------------------------------------ calibration */

/** A presigned URL that changes on every refresh would reload the picture each time: keep one until it is 4 minutes old or the picture changes. */
function useSteadyUrl(url: string | null, identity: string): string | null {
  const held = useRef<{ url: string | null; identity: string; at: number }>({ url, identity, at: Date.now() });
  const h = held.current;
  if (h.identity !== identity || Date.now() - h.at > 240_000 || (!h.url && url)) {
    held.current = { url, identity, at: Date.now() };
  }
  return held.current.url;
}

function Calibration({ cal, calibrated }: { cal: PipelineDetail["calibration"]; calibrated: boolean }) {
  const o = cal.overlay;
  const overlayUrl = useSteadyUrl(o?.url ?? null, o ? `${o.job_id}/${o.attempt}/${o.at}` : "none");
  const frameUrl = useSteadyUrl(cal.frame_url, "frame");
  return (
    <section data-testid="pipeline-calibration">
      <h3>Calibration</h3>
      {o && overlayUrl ? (
        <figure className="mt10">
          <a href={overlayUrl} target="_blank" rel="noreferrer"><img className="calib-img" src={overlayUrl} alt="The calibration frame with the pitch, creases, stumps and ZN crops drawn on it" /></a>
          <figcaption className="tiny mt6">
            Drawn by the pipeline {o.at ? ago(o.at) : ""} · {taskWords(o.task)}, attempt {o.attempt}{o.note ? ` · ${o.note}` : ""}
          </figcaption>
        </figure>
      ) : frameUrl ? (
        <figure className="mt10">
          <img className="calib-img" src={frameUrl} alt="The calibration frame, not annotated yet" />
          <figcaption className="tiny mt6">
            {calibrated
              ? "The plain frame from ingest. No job has drawn the annotated frame for this video yet; the next calibration or analysis will."
              : "Not calibrated yet: the plain frame from ingest."}
          </figcaption>
        </figure>
      ) : (
        <p className="tiny mt10">No calibration frame yet.</p>
      )}
    </section>
  );
}

/**
 * An ingest_failed video, prepared again from the file the user uploaded
 * (POST /ops/videos/:id/reingest): no re-upload, the next revision, the
 * allowance reserved again. "On a GPU pod" is for when the ingest workers run
 * older code than the change the video needs (e.g. the camera-shift check).
 */
function Reingest({ videoId, reload }: { videoId: string; reload: () => Promise<void> }) {
  const [gpu, setGpu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    if (!window.confirm(`Prepare this video again from the file the user uploaded${gpu ? ", on a GPU pod" : ""}? It counts against the workspace's allowance again.`)) return;
    setBusy(true);
    setError(null);
    try {
      await post(`/ops/videos/${videoId}/reingest`, { pool: gpu ? "gpu" : "cpu" });
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt10" data-testid="pipeline-reingest">
      <button className="btn sm" onClick={() => void go()} disabled={busy}>{busy ? "Queuing…" : "Prepare it again from the upload"}</button>
      <label className="tiny row mt6" style={{ gap: 6 }}>
        <input type="checkbox" checked={gpu} onChange={(e) => setGpu(e.target.checked)} /> on a GPU pod (when the ingest workers run older code)
      </label>
      {error && <p className="err mt6">{error}</p>}
    </div>
  );
}
